import { useMemo } from "react";
import type { CronJobView } from "@agentdash/shared";
import { useRepos } from "../repos/hooks.js";
import { describeCron, nextFireDate } from "../../lib/cron-format.js";
import { formatTimeUntil } from "../../lib/time.js";
import { useNow } from "../../lib/use-now.js";

interface FireGroup {
  at: Date;
  jobs: CronJobView[];
}

export function UpcomingRuns() {
  const { data, isLoading, isError, error } = useRepos();
  const now = useNow();

  // `nextFireDate` is evaluated *inside* the memo, so the groups are only as
  // fresh as its deps. Keyed on `data` alone they never self-heal: TanStack's
  // structural sharing keeps `data` referentially stable across refetches that
  // change nothing, so a fire time that passes sticks at "due now" and the next
  // real fire never shifts in. In practice the agent's `last_seen_at` moves on
  // every snapshot (~30s), which bounds the staleness — but not while the agent
  // is down, which is exactly when a row claiming a past fire misleads most.
  // Re-key on the current minute instead: cron's own granularity, so it can't
  // hide a fire, while still not redoing the grouping on every useNow() tick
  // (the countdown text re-renders cheaply on its own).
  const nowMinute = Math.floor(now.getTime() / 60_000);
  const groups = useMemo<FireGroup[]>(() => {
    // `nowMinute` is a recompute trigger, not an input to the grouping below.
    // Referencing it here keeps exhaustive-deps satisfied without a rule-wide
    // disable, so the memo still gets flagged if a future edit reads something
    // new and forgets the dep — the same class of staleness fixed above.
    void nowMinute;
    if (!data) return [];
    const all = [
      ...data.repos.flatMap((r) => r.cron_jobs),
      ...data.ungrouped.cron_jobs,
    ];
    const fires = all
      .filter((j) => j.enabled)
      .map((j) => ({ job: j, at: nextFireDate(j.schedule, j.agent_timezone) }))
      .filter((x): x is { job: CronJobView; at: Date } => x.at !== null);

    const buckets = new Map<number, CronJobView[]>();
    for (const f of fires) {
      const ms = f.at.getTime();
      const arr = buckets.get(ms) ?? [];
      arr.push(f.job);
      buckets.set(ms, arr);
    }
    return [...buckets.entries()]
      .sort(([a], [b]) => a - b)
      .slice(0, 3)
      .map(([ms, jobs]) => ({ at: new Date(ms), jobs }));
  }, [data, nowMinute]);

  return (
    <section className="panel">
      <div className="panel-header">Upcoming runs</div>
      {isLoading ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>Loading…</div>
      ) : isError ? (
        <div style={{ padding: 16, color: "var(--err)" }}>
          Failed to load upcoming runs:{" "}
          {String((error as Error).message ?? error)}
        </div>
      ) : groups.length === 0 ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>
          No upcoming runs (no enabled cron jobs).
        </div>
      ) : (
        groups.map((g) => <FireGroupRow key={g.at.getTime()} group={g} now={now} />)
      )}
    </section>
  );
}

function FireGroupRow({ group, now }: { group: FireGroup; now: Date }) {
  // Render the absolute time in the browser's TZ. Include the date prefix
  // when the fire isn't today so a bare "09:00" doesn't get misread as today.
  const isToday = sameLocalDay(group.at, now);
  const absolute = isToday
    ? group.at.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      })
    : group.at.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

  // Tooltip on the absolute time: list each job's host TZ when known. Lets
  // the user sanity-check that the next fire matches the host's wall clock.
  const tzs = [
    ...new Set(
      group.jobs
        .map((j) => j.agent_timezone)
        .filter((tz): tz is string => !!tz),
    ),
  ];
  const tooltip =
    tzs.length > 0
      ? `${group.at.toISOString()} (host TZ${tzs.length > 1 ? "s" : ""}: ${tzs.join(", ")})`
      : `${group.at.toISOString()} (host TZ not reported; parsed in browser TZ)`;

  return (
    <div
      style={{
        padding: "10px 16px",
        borderTop: "1px solid var(--border)",
        display: "grid",
        gap: 6,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          fontSize: 13,
        }}
      >
        <span style={{ fontWeight: 600 }}>{formatTimeUntil(group.at, now)}</span>
        <span title={tooltip} style={{ color: "var(--muted)", fontSize: 12 }}>
          · {absolute}
        </span>
      </div>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          display: "grid",
          gap: 3,
        }}
      >
        {group.jobs.map((j) => (
          <li
            key={j.cron_job_id}
            className="upcoming-job"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 200px) minmax(0, 180px) 1fr",
              gap: 10,
              fontSize: 12,
              color: "var(--muted)",
              minWidth: 0,
            }}
          >
            <span
              title={j.repo_id ?? "ungrouped"}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {j.repo_id ?? "ungrouped"}
            </span>
            <span
              title={j.schedule}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {describeCron(j.schedule)}
            </span>
            <code
              title={j.command}
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {j.relative_path ?? j.command}
            </code>
          </li>
        ))}
      </ul>
    </div>
  );
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
